export const ORDER_QUOTE_V2_CONTRACT = 'order-entry-v2-quote-v4'

export function withApiToken (config = {}, storage) {
  const tokenStorage = storage || (typeof window !== 'undefined' ? window.localStorage : null)
  const token = tokenStorage && tokenStorage.getItem ? tokenStorage.getItem('api_token') : null
  const params = { ...(config.params || {}) }
  const validateStatus = config.validateStatus || (status => status >= 200 && status < 300)
  if (token) params.token = token

  return {
    ...config,
    params,
    // A quote authorization failure belongs in the quote retry UI. Treat it as a
    // response until the component can handle it, so ACE's global rejected-401
    // interceptor does not redirect an otherwise valid session to Login.
    validateStatus: status => status === 401 || status === 403 || validateStatus(status)
  }
}

export function quoteResponseError (response) {
  if (!response || (response.status !== 401 && response.status !== 403)) return null
  const error = new Error('The quote request was not authorized.')
  error.response = response
  return error
}

function clone (value) {
  return JSON.parse(JSON.stringify(value))
}

function idOf (value, nestedKey) {
  if (!value) return 0
  return Number(value[nestedKey] || value.id || 0) || 0
}

function sanitizeSizes (sizes) {
  return (sizes || []).map(size => ({
    size_id: idOf(size, 'size_id') || idOf(size.size, 'size_id'),
    size_name: size.size_name || size.size?.size_name || null,
    quantity: Math.max(0, parseInt(size.quantity || 0, 10) || 0)
  }))
}

function sanitizeColor (color) {
  return {
    color_id: idOf(color, 'color_id') || idOf(color.color, 'color_id'),
    color_code: color.color_code || color.color?.color_code || null,
    color_name: color.color_name || color.color?.color_name || null,
    customer_color_code: color.customer_color_code || null,
    customer_color_name: color.customer_color_name || null,
    sizes: sanitizeSizes(color.sizes || color.order_item_sizes),
    inner_colors: (color.inner_colors || []).map(sanitizeColor)
  }
}

function sanitizeLocation (location) {
  return {
    id: location.decoration_key || location.id || location.location_reference || null,
    decoration_key: location.decoration_key || location.id || location.location_reference || null,
    technique_id: idOf(location, 'technique_id') || idOf(location.technique, 'technique_id'),
    technique_type: location.technique_type || location.technique?.technique_type || location.technique?.type || null,
    location_id: idOf(location, 'location_id') || idOf(location.location, 'location_id'),
    product_class: location.product_class || location.location?.product_class || null,
    resolution_source: location.resolution_source || null,
    manual_confirmation: !!location.manual_confirmation,
    configuration_key: location.configuration_key || null,
    ptl_identity: location.ptl_identity || location.location?.ptl_identity || null,
    ptl_program_id: Number(location.ptl_program_id || location.location?.ptl_program_id || 0) || null,
    ptl_technique_id: Number(location.ptl_technique_id || location.location?.ptl_technique_id || 0) || null,
    ptl_location_id: Number(location.ptl_location_id || location.location?.ptl_location_id || 0) || null,
    technique_internal_id: Number(location.technique_internal_id || location.technique?.internal_id || 0) || null,
    location_internal_id: Number(location.location_internal_id || location.location?.internal_id || 0) || null,
    selected_configuration_label: location.selected_configuration_label || null
  }
}

function sanitizeItem (item) {
  return {
    id: item.id || item.order_item_id || null,
    section_reference: item.section_reference || item.section_id || item.temp_section_name || null,
    style_id: idOf(item, 'style_id') || idOf(item.style_info, 'style_id'),
    product_class: item.product_class || item.style_info?.product_class || null,
    blank_order: !!Number(item.blank_order || 0),
    section_type: item.section_type,
    promotion_applied: !!item.promotion_applied,
    promotion_data: item.promotion_data ? clone(item.promotion_data) : null,
    colors: (item.colors || item.order_item_colors || []).map(sanitizeColor),
    locations: (item.locations || item.order_item_locations || []).map(sanitizeLocation)
  }
}

export function buildOrderQuoteV2Payload (submitPreview) {
  const source = clone(submitPreview || {})
  const groups = (source.groups || []).map(group => ({
    id: group.group_id || group.id || null,
    program_id: idOf(group, 'program_id') || idOf(group.program_info, 'program_id'),
    tier: group.tier === undefined ? null : group.tier,
    selected_promotion: group.selected_promotion && group.selected_promotion.id
      ? { id: Number(group.selected_promotion.id) }
      : null,
    additional_charges: (group.additional_charges || []).map(charge => ({
      additional_charges_id: idOf(charge, 'additional_charges_id'),
      location: charge.location || null
    })),
    royalties: (group.royalties || group.royalty_charges || []).map(royalty => ({
      id: idOf(royalty, 'id') || idOf(royalty, 'royalty_id')
    })),
    items: (group.order_items || group.items || []).map(sanitizeItem)
  }))
  return {
    customer: clone(source.customer || source.customer_info || {}),
    blank_recovery: clone(source.blank_recovery || []),
    prebook: source.prebook ? clone(source.prebook) : null,
    selected_promotion_ids: groups.map(group => group.selected_promotion?.id).filter(Boolean),
    groups
  }
}

export function validOrderQuoteV2Response (response) {
  return !!(
    response &&
    response.calculation_version === ORDER_QUOTE_V2_CONTRACT &&
    response.currency === 'USD' &&
    Array.isArray(response.groups) &&
    response.totals && (
      (response.totals.quoted_order_total !== null && Number.isFinite(Number(response.totals.quoted_order_total))) ||
      (response.authoritative === false && response.totals.quoted_order_total === null)
    ) &&
    response.promotions && typeof response.promotions === 'object'
  )
}

export function shouldApplyOrderQuoteV2Response (requestSequence, currentSequence) {
  return requestSequence === currentSequence
}

export function promotionResultsForDisplay (promotions) {
  const order = ['available', 'targeting', 'nearly_qualified', 'qualified', 'applied', 'earned', 'ineligible']
  return order.reduce((rows, bucket) => rows.concat((promotions?.[bucket] || []).map(promotion => ({
    ...promotion,
    bucket
  }))), [])
}

export function quoteErrorMessage (error) {
  const status = error?.response?.status
  if (status === 401 || status === 403) return 'Your session is not authorized to refresh this quote.'
  if (status === 422) return 'The order is missing information required for an authoritative quote.'
  if (status && status >= 500) return 'The quote service is temporarily unavailable.'
  return 'Unable to refresh the authoritative quote.'
}
